<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { differenceInCalendarDays, format } from 'date-fns';
import ButtonV4 from 'next/button/Button.vue';
import {
  currencyForBillingRegion,
  formatCurrencyAmount,
  planFeatureLabels,
} from 'dashboard/constants/billing';

const props = defineProps({
  planName: { type: String, default: '' },
  prices: { type: Object, default: () => ({}) },
  // Limits the plan does not set are uncapped.
  limits: { type: Object, default: () => ({}) },
  features: { type: Array, default: () => [] },
  billingRegion: { type: String, default: '' },
  planExpiresAt: { type: String, default: '' },
  trialEndsAt: { type: String, default: '' },
});

defineEmits(['manage']);

const { t } = useI18n();

const currency = computed(() => currencyForBillingRegion(props.billingRegion));

const price = computed(() => {
  const amount = props.prices[currency.value];
  if (!amount) return '';
  return t('BILLING_SETTINGS.PACKAGES.PER_MONTH', {
    price: formatCurrencyAmount(amount, currency.value, {
      maximumFractionDigits: 0,
    }),
  });
});

// A paid renewal date always wins: a customer who pays mid-trial keeps a
// trial_ends_at that has not lapsed yet, and they are no longer on trial.
const renewalLabel = computed(() => {
  if (props.planExpiresAt) {
    return t('BILLING_SETTINGS.PLAN.RENEWS_ON', {
      date: format(new Date(props.planExpiresAt), 'dd MMM, yyyy'),
    });
  }
  if (!props.trialEndsAt) return '';

  const daysLeft = differenceInCalendarDays(
    new Date(props.trialEndsAt),
    new Date()
  );
  if (daysLeft <= 0) return t('BILLING_SETTINGS.PLAN.TRIAL_ENDS_TODAY');
  return t('BILLING_SETTINGS.PLAN.TRIAL', { count: daysLeft });
});

const checklist = computed(() => [
  props.limits.agents
    ? t('BILLING_SETTINGS.PACKAGES.AGENTS', { count: props.limits.agents })
    : t('BILLING_SETTINGS.PLAN.UNLIMITED_AGENTS'),
  props.limits.inboxes
    ? t('BILLING_SETTINGS.PACKAGES.INBOXES', { count: props.limits.inboxes })
    : t('BILLING_SETTINGS.PACKAGES.UNLIMITED_INBOXES'),
  props.limits.ai_credits
    ? t('BILLING_SETTINGS.PACKAGES.CREDITS', {
        count: props.limits.ai_credits,
      })
    : t('BILLING_SETTINGS.PLAN.UNLIMITED_CREDITS'),
  ...planFeatureLabels(props.features),
]);
</script>

<template>
  <div
    class="rounded-xl shadow-sm border border-n-weak bg-n-solid-2 py-5 space-y-5"
  >
    <div class="flex flex-wrap items-start justify-between gap-3 px-5">
      <div class="flex flex-col gap-1">
        <div class="flex flex-wrap items-baseline gap-2">
          <h3 class="text-lg font-medium text-n-slate-12">
            {{ planName }}
          </h3>
          <span v-if="price" class="text-sm text-n-slate-11">{{ price }}</span>
        </div>
        <span v-if="renewalLabel" class="text-sm text-n-slate-11">
          {{ renewalLabel }}
        </span>
      </div>
      <ButtonV4 sm solid blue @click="$emit('manage')">
        {{ $t('BILLING_SETTINGS.PLAN.MANAGE') }}
      </ButtonV4>
    </div>
    <ul class="grid grid-cols-1 gap-2 px-5 sm:grid-cols-2 lg:grid-cols-3">
      <li
        v-for="item in checklist"
        :key="item"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <span class="i-lucide-check size-4 shrink-0 text-n-teal-10" />
        {{ item }}
      </li>
    </ul>
  </div>
</template>
